import { useCallback } from 'react';

import { useCheckNetworkAccess } from '@/lib/acl/useCheckNetworkAccess';

/**
 * Hook to check if a social network has access to a specific page.
 * Handles both standard access rules and feature flag overrides.
 *
 * @returns An object with a checkAccess function
 *
 * @example
 * const { checkAccess } = useMenuEntryAccess();
 * const hasAccess = checkAccess('instagram', '/analyze/overview');
 */
export const useMenuEntryAccess = () => {
    const { checkAccess: checkAccessBase } = useCheckNetworkAccess();

    /**
     * Check if a social network has access to a specific page
     * @param socialNetworkType - The social network type (e.g., 'instagram', 'facebook')
     * @param targetUrl - The target URL to check access for
     * @returns true if the network has access, false otherwise
     */
    const checkAccess = useCallback(
        (socialNetworkType: string, targetUrl: string): boolean => {
            return checkAccessBase(socialNetworkType, targetUrl);
        },
        [checkAccessBase]
    );

    return { checkAccess };
};
